import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPanelHeader from '../../components/common/Header/AdminPanelHeader';
import Footer from '../../components/common/Footer/Footer';
import SiridhariniBlogService from '../../services/siridhariniBlogService';

const SiridhariniBlogManagement = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      // In a real implementation, you'd have an admin endpoint
      // For now, we'll use the public endpoint
      const blogsData = await SiridhariniBlogService.getAllPublishedBlogs();
      setBlogs(blogsData);
    } catch (err) {
      setError('Failed to load blogs');
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '60vh'
    },
    header: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '2rem',
      marginBottom: '2rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    title: {
      color: '#4A5D23',
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1rem'
    },
    subtitle: {
      color: '#666',
      fontSize: '1.1rem',
      marginBottom: '2rem'
    },
    uploadButton: {
      backgroundColor: '#4A5D23',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '8px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      display: 'inline-block'
    },
    blogsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem'
    },
    blogCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease'
    },
    blogTitle: {
      color: '#4A5D23',
      fontSize: '1.2rem',
      fontWeight: '600',
      marginBottom: '0.5rem'
    },
    blogMeta: {
      color: '#666',
      fontSize: '0.9rem',
      marginBottom: '1rem'
    },
    blogExcerpt: {
      color: '#444',
      fontSize: '0.95rem',
      lineHeight: '1.6',
      marginBottom: '1rem'
    },
    blogActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    actionButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      border: 'none',
      fontSize: '0.85rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    editButton: {
      backgroundColor: '#3498db',
      color: 'white'
    },
    viewButton: {
      backgroundColor: '#95a5a6',
      color: 'white'
    },
    loading: {
      textAlign: 'center',
      padding: '3rem',
      fontSize: '1.2rem',
      color: '#666'
    },
    error: {
      textAlign: 'center',
      padding: '3rem',
      fontSize: '1.2rem',
      color: '#dc2626'
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      color: '#666'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <AdminPanelHeader />
        <div style={styles.content}>
          <div style={styles.loading}>Loading blogs...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <AdminPanelHeader />
        <div style={styles.content}>
          <div style={styles.error}>{error}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AdminPanelHeader />
      
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>SiriDharini Blog Management</h1>
          <p style={styles.subtitle}>Manage your blog content and create new posts</p>
          
          <button
            style={styles.uploadButton}
            onClick={() => navigate('/adminportal/siridharini-blog-upload')}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#3d4f1c'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#4A5D23'}
          >
            + Create New Blog Post
          </button>
        </div>

        {blogs.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No blogs found</h3>
            <p>Start by creating your first blog post!</p>
          </div>
        ) : (
          <div style={styles.blogsGrid}>
            {blogs.map((blog) => (
              <div 
                key={blog.id} 
                style={styles.blogCard}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <h3 style={styles.blogTitle}>{blog.title}</h3>
                <div style={styles.blogMeta}>
                  {blog.category} • {blog.readTime} • {blog.createdAt}
                </div>
                <p style={styles.blogExcerpt}>
                  {blog.excerpt || 'No excerpt available'}
                </p>
                <div style={styles.blogActions}>
                  <button 
                    style={{...styles.actionButton, ...styles.viewButton}}
                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                  >
                    View
                  </button>
                  <button 
                    style={{...styles.actionButton, ...styles.editButton}}
                    onClick={() => navigate(`/adminportal/siridharini-blog-upload?edit=${blog.id}`)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default SiridhariniBlogManagement;